import useAnalyticsStore from '~/stores/analytics';
import { useAnalytics } from '~/composables/useAnalytics';
import { isLoadingTitle, titleObserverConfig } from '~/functions/isLoadingTitle';

export default defineNuxtPlugin((nuxtApp: any) => {
  const analyticsStore = useAnalyticsStore();
  const { trackPageView } = useAnalytics();

  // Initialize the analytics system
  analyticsStore.initializeAnalytics();

  // TDD: Set up SPA referrer tracking using router hooks
  let pendingReferrer: string | null = null;
  
  nuxtApp.$router.beforeEach((to: any, from: any) => {
    // Capture the referrer for the upcoming navigation
    // Set referrer if we have a meaningful previous route
    if (from.fullPath && from.name) {
      pendingReferrer = from.fullPath;
    } else {
      pendingReferrer = null;
    }
  });


  // Enhanced page tracking using your existing gtag patterns
  const trackPageViewWhenReady = () => {
    let hasTracked = false;
    let attempts = 0;
    const maxAttempts = 50; // Poll for 5 seconds max (50 * 100ms)
    const initialTitle = document.title; // Capture stale title from previous page

    const doTrack = (title: string) => {
      if (hasTracked) return;
      hasTracked = true;
      trackPageView({
        page_path: window.location.pathname,
        page_title: title,
        referrer: pendingReferrer || document.referrer || undefined,
      });
    };

    // Poll for title changes using intervals
    const pollForTitle = () => {
      const currentTitle = document.title;
      attempts++;

      const titleChanged = currentTitle !== initialTitle;
      const titleReady = !isLoadingTitle(currentTitle);
      const initialWasLoading = isLoadingTitle(initialTitle);

      // Track when title is not a loading state AND either:
      // - Title changed from previous page, OR
      // - Initial title was a loading state, OR
      // - First poll and title is already ready (static page where title is set before page:finish)
      if (titleReady && (titleChanged || initialWasLoading || attempts === 1)) {
        doTrack(currentTitle);
        return;
      }

      // Continue polling if we haven't exceeded max attempts
      if (attempts < maxAttempts) {
        setTimeout(pollForTitle, 100);
      }
    };

    // Start polling immediately
    pollForTitle();
  };

  // Hook into page changes (using your existing gtag hook pattern)
  nuxtApp.hook('page:finish', () => {
    // Use universal tracking for ALL pages - no special cases
    trackPageViewWhenReady();
  });

  // Provide analytics functions globally if needed
  return {
    provide: {
      analytics: {
        trackPageView,
        getStats: () => analyticsStore,
      }
    }
  };
});